import clsx from 'clsx'
import { default as PicklistField } from 'core/components/validatedForm/DropdownField'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import Info from 'core/components/validatedForm/Info'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Slider from 'core/elements/Slider'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import ExternalLink from 'core/components/ExternalLink'
import Alert from 'core/components/Alert'
import useListAction from 'core/hooks/useListAction'
import { yamlValidator } from 'core/utils/fieldValidators'
import { drrDoc, serverGroupsDoc } from 'app/docLinks'
import { useSelector } from 'react-redux'
import {
  countValidator,
  getDisableMultipleCreateVmsState,
} from '../CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import MigrationPriorityPicklist from '../MigrationPriorityPicklist'
import SshKeyPicklist from '../SshKeyPicklist'
import { securityGroupColumns } from '../helpers'
import ServerGroupsPicklist from '../server-groups/ServerGroupsPicklist'
import { ENABLE_SIMPLE_NETWORK_BATCH_CREATION } from '../hooks/useNetworkPorts'
import { NetworkMode } from '../../networks/constants'
import { listSecurityGroups } from '../../networks/security-groups/actions'
import { securityGroupsSelector } from '../../networks/security-groups/selectors'

interface Props {
  getParamsUpdater: any
  loadingSecurityGroups: boolean
  minAvailableIpsNetwork: any
  params: any
  securityGroupsList: any[]
  updateParams: any
  vmNetworkMode: NetworkMode | null
}

export function useCreateVirtualMachineCustomizeStepController() {
  const { loading: loadingSecurityGroups } = useListAction(listSecurityGroups, {
    params: {},
  })
  const securityGroupsList = useSelector(securityGroupsSelector)

  return {
    loadingSecurityGroups,
    securityGroupsList,
  }
}

export default function CreateVirtualMachineCustomizeStep({
  getParamsUpdater,
  loadingSecurityGroups,
  minAvailableIpsNetwork,
  params,
  securityGroupsList,
  updateParams,
  vmNetworkMode,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const disableMultipleVMs = getDisableMultipleCreateVmsState(
    params,
    ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  )

  return (
    <div className={classes.fields}>
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Customize VM</Text>
          <Text variant="body2">
            Set advanced options like SSH keys, user data, security groups, VM metadata and optional
            GPU lease time.
          </Text>
        </div>
      </Info>

      {minAvailableIpsNetwork?.availablePortAndIPs < params.count &&
        vmNetworkMode === NetworkMode.MANAGED && (
          <Alert
            variant="warning"
            message={
              <Text variant="body2">
                Not enough available IPs or ports. Managed Network
                <Text variant="caption1"> {minAvailableIpsNetwork?.name} </Text>
                only contains {minAvailableIpsNetwork?.availablePortAndIPs} available IPs or ports.
                Please select another network or select VM count between 1 and{' '}
                {minAvailableIpsNetwork?.availablePortAndIPs}.
              </Text>
            }
          />
        )}
      {disableMultipleVMs.disabled && (
        <Alert
          variant="primary"
          message={<Text variant="body2">{disableMultipleVMs.message}</Text>}
        />
      )}

      <div>
        <Text
          variant="body2"
          component="div"
          className={clsx(classes.tooltipContainer, {
            [classes.disabledText]: disableMultipleVMs.disabled,
          })}
        >
          Number of VMs
          <HintTooltip message="Specify the number of VMs you’d like to create using this configuration" />
        </Text>
        <div
          className={clsx(classes.vmCount, {
            [classes.vmCountDisabled]: disableMultipleVMs.disabled,
          })}
        >
          <Slider
            min={1}
            max={disableMultipleVMs.disabled ? 1 : 20}
            minLabel="1 VM"
            maxLabel="20 VMs"
            step={1}
            onChange={(value) =>
              updateParams({
                count: value,
              })
            }
            value={params.count}
            disabled={disableMultipleVMs.disabled}
          />
          <div className={classes.vmCountInput}>
            <TextField
              id="count"
              label=""
              onChange={(value) =>
                updateParams({
                  count: value,
                })
              }
              value={params.count}
              type="number"
              min={1}
              max={20}
              disabled={disableMultipleVMs.disabled}
              validations={[countValidator]}
              required
            />
            <Text
              variant="body2"
              className={clsx(classes.vmCountText, {
                [classes.disabledText]: disableMultipleVMs.disabled,
              })}
            >
              VMs
            </Text>
          </div>
        </div>
      </div>
      <div className={classes.halfColumns}>
        <PicklistField
          id="sshKey"
          DropdownComponent={SshKeyPicklist}
          value={params.sshKey}
          onChange={getParamsUpdater('sshKey')}
          // @ts-ignore
          info={
            <div>Select the ssh key that will be injected into the VM to enable ssh access</div>
          }
        />
        <PicklistField
          id="serverGroup"
          DropdownComponent={ServerGroupsPicklist}
          value={params.serverGroup}
          onChange={getParamsUpdater('serverGroup')}
          info={
            <div>
              Select what VM affinity or anti-affinity group this VM should be part of.{' '}
              <ExternalLink className={classes.tooltipLink} url={serverGroupsDoc}>
                More info
              </ExternalLink>
            </div>
          }
        />
      </div>
      <div className={classes.separator} />
      <ToggleSwitchField
        id="useCloudInit"
        value={params.useCloudInit}
        onChange={getParamsUpdater('useCloudInit')}
        label="Use Cloud-init"
        disabled={params.sourceType === 'installFromIso'}
        tooltipMsg={
          params.sourceType === 'installFromIso'
            ? 'ISO images do not include a cloud-init agent, so cloud-init configuration is not available during installation.'
            : undefined
        }
        textDescription={
          'Add user data for initial VM setup, such as installing packages, or configuring users. This will run on first boot.'
        }
      />
      {params.useCloudInit && (
        <div className={classes.tabbed}>
          <CodeMirror
            variant="light"
            label="Cloud-init YAML"
            id="cloudInitYaml"
            showCopyButton
            onChange={getParamsUpdater('cloudInitYaml')}
            value={params.cloudInitYaml}
            validations={[yamlValidator]}
          />
        </div>
      )}
      <ToggleSwitchField
        id="useSecurityGroups"
        value={params.useSecurityGroups}
        onChange={getParamsUpdater('useSecurityGroups')}
        label="Assign Security Groups"
        disabled={vmNetworkMode === NetworkMode.SIMPLE}
        textDescription={
          'Choose one or more security groups to be applied to the VM, to ensure consistent traffic rules across all network interfaces attached to the VM.'
        }
      />
      {params.useSecurityGroups && vmNetworkMode !== NetworkMode.SIMPLE && (
        <div className={classes.tabbed}>
          <ListTableField
            id="securityGroups"
            data={securityGroupsList}
            loading={loadingSecurityGroups}
            columns={securityGroupColumns}
            onChange={getParamsUpdater('securityGroups')}
            value={params.securityGroups}
            uniqueIdentifier="id"
            searchTargets={['name']}
            multiSelection
            required
          />
        </div>
      )}
      <div className={classes.separator} />
      <Text variant="subtitle2">Set Migration Priority</Text>
      <Text variant="body2">
        The migration priority determines whether the virtual machine qualifies as a candidate for
        migration by the DRR service, and, if it does, specifies the priority level for its
        migration. <ExternalLink url={drrDoc}>More info</ExternalLink>
      </Text>
      <PicklistField
        id="priority"
        DropdownComponent={MigrationPriorityPicklist}
        value={params.priority}
        onChange={getParamsUpdater('priority')}
      />
      <div className={classes.separator} />
      <KeyValuesField
        id="metadata"
        label="Metadata"
        value={params.metadata}
        onChange={getParamsUpdater('metadata')}
        addLabel="Add Metadata"
        allowMultipleValues
      />
    </div>
  )
}
